import {Component, Input, OnInit} from '@angular/core';
import {ProductoPlazoInversionService} from '../../../../_services/producto-plazo-inversion.service';
import {Producto} from '../../../../_models/producto';
import {ProductoPlazoInversion} from '../../../../_models/producto-plazo-inversion';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NavService} from '../../../../_services/_nav.service';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {CanalService} from '../../../../_services/canal.service';
import {firstValueFrom, forkJoin, from} from 'rxjs';
import {Canal} from '../../../../_models/canal';
import {ProductoPlazoInversionCanalService} from '../../../../_services/producto-plazo-inversion-canal.service';
import {ProductoPlazoInversionCanal} from '../../../../_models/producto-plazo-inversion-canal';
import {CustomValidators} from '../../../../_helpers/formularios-reactivos/custom.validators';

@Component({
  selector: 'app-producto-plazos',
  templateUrl: './producto-plazos.component.html',
  styleUrl: './producto-plazos.component.css'
})
export class ProductoPlazosComponent implements OnInit {
  @Input() producto: Producto;
  @Input() isDetail: boolean = false;
  productoPlazosInversion: ProductoPlazoInversion[] = [];
  productoPlazosInversionCanales: ProductoPlazoInversionCanal[] = [];
  canales: Canal[] = [];
  canalesSelect: SelectItem[] = [];
  loading: boolean = true;
  cols: any = [];
  rows: number = 10;
  productoPlazoInversionSelected: ProductoPlazoInversion;
  form: FormGroup;
  submitted: boolean = false;
  saving: boolean = false;

  constructor(
    private productoPlazoInversionService: ProductoPlazoInversionService,
    private productoPlazoInversionCanalService: ProductoPlazoInversionCanalService,
    private canalService: CanalService,
    private navServicio: NavService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.cols = [
      { field: 'rangoInicial', header: this.navServicio.buscarMensaje('PRODUCTO.LBL_INICIO_RANGO'), sort: false },
      { field: 'rangoFinal', header: this.navServicio.buscarMensaje('PRODUCTO.LBL_FIN_RANGO'), sort: false },
      { field: 'canalesNombres', header: this.navServicio.buscarMensaje('PRODUCTO.LBL_CANAL'), sort: false },
    ];

    if(!this.isDetail) {
      this.cols.push({ field: 'acciones', header: this.navServicio.buscarMensaje('LBL_ACCIONES'), sort: false });
    }

    forkJoin([
      from(this.loadProductoPlazos()),
      this.canalService.listadoCanales(),
    ]).subscribe({
      next: ([, canales]) => {
        this.canales = canales;
        this.canalesSelect = canales.map(e => ({ label: e.nombre, value: e.idCanal }));
        this.loadCanalesNombres();
        this.loadForm();
        this.loading = false;
      },
    });
  }

  loadForm(): void {
    const idsCanales: string[] = this.productoPlazoInversionSelected
      ? this.productoPlazosInversionCanales
        .filter(e => e.idProductoPlazoInversion === this.productoPlazoInversionSelected.idProductoPlazoInversion)
        .map(e => e.idCanal)
      : [];

    this.form = this.fb.group({
      idProducto: [ this.producto.idProducto ],
      idProductoPlazoInversion: [ this.productoPlazoInversionSelected ? this.productoPlazoInversionSelected.idProductoPlazoInversion : null ],
      rangoInicial: [ this.productoPlazoInversionSelected ? this.productoPlazoInversionSelected.rangoInicial : null, [Validators.required] ],
      rangoFinal: [ this.productoPlazoInversionSelected ? this.productoPlazoInversionSelected.rangoFinal : null, [Validators.required] ],
      idsCanales: [ idsCanales, [Validators.required] ],
    }, {
      validators: [
        CustomValidators.rangeSequence({ startControlName: 'rangoInicial', endControlName: 'rangoFinal' }),
        CustomValidators.rangeOverlap<ProductoPlazoInversion>({
          startControlName: 'rangoInicial',
          endControlName: 'rangoFinal',
          idControlName: 'idProductoPlazoInversion',
          ranges: this.productoPlazosInversion,
        }),
      ],
    });
  }

  get f() {
    return this.form.controls;
  }

  clearForm(): void {
    this.productoPlazoInversionSelected = null;
    this.submitted = false;
    this.loadForm();
  }

  onEdit(row: ProductoPlazoInversion): void {
    this.productoPlazoInversionSelected = row;
    this.submitted = false;
    this.loadForm();
  }

  onCancel(): void {
    if(this.form.dirty) {
      this.confirmationService.confirm({
        message: this.navServicio.buscarMensaje('MSG_CANCELARCAMBIOS'),
        header: this.navServicio.buscarMensaje('LBL_CONFIRMACION'),
        icon: 'fa fa-question-circle',
        key: 'producto-plazos-inversion',
        accept: () => this.clearForm(),
      });
    } else {
      this.clearForm();
    }
  }

  onDelete(row: ProductoPlazoInversion): void {
    this.confirmationService.confirm({
      message: this.navServicio.buscarMensaje('MSG_INHABILITAR'),
      header: this.navServicio.buscarMensaje('LBL_CONFIRMACION'),
      icon: 'fa fa-question-circle',
      key: 'producto-plazos-inversion',
      accept: () => {
        this.saving = true;
        row.indicadorHabilitado = false;
        this.plazoInversionUpdate(row, true);
      },
    });
  }

  onSubmit(): void {
    this.submitted = true;
    if(this.form.invalid) return;

    this.saving = true;
    const data: ProductoPlazoInversion = this.form.value;
    if(data.idProductoPlazoInversion) {
      this.plazoInversionUpdate(data);
    } else {
      this.productoPlazoInversionService.create(data).subscribe({
        next: async (response: ProductoPlazoInversion) => {
          await this.plazosInversionCanalesBulk(response);
          await this.loadProductoPlazos();
          this.navServicio.setMesage(1);
          this.clearForm();
          this.saving = false;
        },
        error: error => {
          this.navServicio.setMesage(3);
          this.saving = false;
        }
      });
    }
  }

  plazoInversionUpdate(data: ProductoPlazoInversion, isDelete?: boolean): void {
    this.productoPlazoInversionService.update(data).subscribe({
      next: async (response: ProductoPlazoInversion) => {
        if(!isDelete) {
          await this.plazosInversionCanalesBulk(data);
        }
        await this.loadProductoPlazos();
        this.navServicio.setMesage(2);
        this.clearForm();
        this.saving = false;
      },
      error: error => {
        this.navServicio.setMesage(3);
        this.saving = false;
      }
    });
  }

  async plazosInversionCanalesBulk(productoPlazo: ProductoPlazoInversion): Promise<ProductoPlazoInversionCanal[]> {
    const idsCanales: string[] = this.form.value.idsCanales;
    const { idProductoPlazoInversion } = productoPlazo;
    const productoPlazosCanales: ProductoPlazoInversionCanal[] = [];

    idsCanales.forEach((idCanal: string) => {
      const productoPlazoCanal: ProductoPlazoInversionCanal = {
        idProductoPlazoInversion,
        idCanal,
        indicadorHabilitado: true,
      } as ProductoPlazoInversionCanal;

      const existente: ProductoPlazoInversionCanal = this.productoPlazosInversionCanales.find(e => e.idProductoPlazoInversion === idProductoPlazoInversion && e.idCanal === idCanal);
      if (existente) {
        productoPlazoCanal.idProductoPlazoInversionCanal = existente.idProductoPlazoInversionCanal;
      }

      productoPlazosCanales.push(productoPlazoCanal);
    });

    this.productoPlazosInversionCanales.forEach(e => {
      if(e.idProductoPlazoInversion === idProductoPlazoInversion && e.idProductoPlazoInversionCanal && e.indicadorHabilitado && !idsCanales.includes(e.idCanal)) {
        e.indicadorHabilitado = false;
        productoPlazosCanales.push(e);
      }
    });

    return await firstValueFrom(this.productoPlazoInversionCanalService.bulk(productoPlazosCanales));
  }

  async loadProductoPlazos(): Promise<void> {
    const [productoPlazosInversion, productoPlazosInversionCanales] = await firstValueFrom(
      forkJoin([
        this.productoPlazoInversionService.getEnabledByIdProducto(this.producto.idProducto),
        this.productoPlazoInversionCanalService.getEnabledByIdProducto(this.producto.idProducto),
      ])
    );

    this.productoPlazosInversion = productoPlazosInversion;
    this.productoPlazosInversionCanales = productoPlazosInversionCanales;
    this.loadCanalesNombres();
  }

  loadCanalesNombres(): void {
    this.productoPlazosInversion.forEach(plazo => {
      const plazosCanales: ProductoPlazoInversionCanal[] = this.productoPlazosInversionCanales.filter(e => e.idProductoPlazoInversion === plazo.idProductoPlazoInversion);

      plazo.canalesNombres = plazosCanales.map(plazoCanal => {
        const canal: Canal = this.canales.find(e => e.idCanal === plazoCanal.idCanal);
        return canal ? canal.nombre : null;
      }).filter(Boolean).join(', ');
    });
  }
}
